import { useRef, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { getInitials } from 'utils/helpers'
import { format } from 'utils/datetime'
import { useAuth, useToggle, useOnClickOutside, useIntl } from 'hooks'
import { IconRefresh } from 'components/Zui/Icon'
import Settings from 'features/settings'
import { hasUnreadSelector } from 'features/bell-notification/notificationSlice'
import { syncPatient } from 'features/sync-data/syncSlice'
import BellNotification from 'features/bell-notification/Notification'
import { Headerbar, HeaderbarIconButton } from 'components/Zui'
import HeaderNotificationIcon from 'components/Notifications/HeaderNotificationIcon'
import Spinner from 'components/spinner'
import UserProfileModal from 'components/UserProfileModal'
import AppVersion from 'components/AppVersion'
import Legal from 'components/Legal'
import Privacy from 'components/Privacy'
import TermsToUse from 'components/TermsToUse'
import Imprint from 'components/Imprint'
import Foss from 'components/Foss'
import HeaderAppName from './HeaderAppName'
import './header.css'

const Header = () => {
    const dispatch = useDispatch()
    const { t } = useIntl()
    const { isAuthorized, authUser } = useAuth()
    const hasUnread = useSelector(hasUnreadSelector)
    const { user: userSyncData } = useSelector(state => state.sync)
    const myRef = useRef(null)
    const bellRef = useRef(null)
    const { isOpen, onClose, onToggle } = useToggle()
    const { isOpen: isBellOpen, onClose: onBellClose, onToggle: onBellToggle } = useToggle()
    const { isOpen: isVersionOpen, onClose: onVersionClose, onOpen: onVersionOpen } = useToggle()
    const { isOpen: isSetOpen, onClose: onSetClose, onOpen: onSetOpen } = useToggle()
    const { isOpen: isLegalOpen, onClose: onLegalClose, onOpen: onLegalOpen } = useToggle()
    const { isOpen: isPrivacyOpen, onClose: onPrivacyClose, onOpen: onPrivacyOpen } = useToggle()
    const {
        isOpen: isTermsToUseOpen,
        onClose: onTermsToUseClose,
        onOpen: onTermsToUseOpen,
    } = useToggle()
    const { isOpen: isImprintOpen, onClose: onImprintClose, onOpen: onImprintOpen } = useToggle()
    const { isOpen: isFossOpen, onClose: onFossClose, onOpen: onFossOpen } = useToggle()

    useOnClickOutside(myRef, onClose, 'headerProfile')
    useOnClickOutside(bellRef, onBellClose, 'bellFlyout')

    const initials = getInitials(authUser.firstName, authUser.lastName)

    const handleSync = () => {
        if (!isAuthorized) return
        dispatch(syncPatient())
    }

    const syncIcon = useMemo(() => {
        return userSyncData.isSyncing ? (
            <Spinner slot="icon" />
        ) : (
            <IconRefresh slot="icon" onClick={handleSync}></IconRefresh>
        )
    }, [userSyncData])

    return (
        <div>
            {isSetOpen ? <Settings onClose={onSetClose} /> : ''}
            {isLegalOpen && <Legal onClose={onLegalClose} />}
            {isPrivacyOpen && <Privacy onClose={onPrivacyClose} />}
            {isTermsToUseOpen && <TermsToUse onClose={onTermsToUseClose} />}
            {isImprintOpen && <Imprint onClose={onImprintClose} />}
            {isFossOpen && <Foss onClose={onFossClose} />}

            <div className="header-wrapper">
                <Headerbar>
                    <zui-zeiss-logo size="s32" slot="icon"></zui-zeiss-logo>
                    <HeaderAppName />

                    {isAuthorized && (
                        <HeaderbarIconButton size="m" slot="iconButtons">
                            <zui-tooltip-directive
                                delayed
                                id="tooltip"
                                trigger="focus,hover"
                                anchoring="component"
                            >
                                <zui-tooltip emphasis="">
                                    {`${t('last.sync')}: ${format(
                                        userSyncData.lastSyncOn,
                                        'yyyy-MM-dd HH:mm'
                                    )}`}
                                </zui-tooltip>
                            </zui-tooltip-directive>
                            {syncIcon}
                        </HeaderbarIconButton>
                    )}

                    <HeaderNotificationIcon
                        onClick={onBellToggle}
                        dataRef="bellFlyout"
                        isOpen={isBellOpen}
                        unread={hasUnread}
                    />
                    <zui-user-menu-button
                        data-ref="headerProfile"
                        size="l"
                        slot="userMenuButton"
                        user-name={`${authUser.firstName} ${authUser.lastName}`}
                        onClick={onToggle}
                        emphasis={isOpen ? 'selected' : 'default'}
                        theme="zbds-light"
                    >
                        <zui-avatar
                            data-ref="headerProfile"
                            initials={initials}
                            slot="avatar"
                        ></zui-avatar>
                    </zui-user-menu-button>
                </Headerbar>
                {isOpen && (
                    <UserProfileModal
                        ref={myRef}
                        onClose={onClose}
                        onOpen={onSetOpen}
                        handleVesionOpen={onVersionOpen}
                        handleLegalOpen={onLegalOpen}
                        handlePrivacyOpen={onPrivacyOpen}
                        handleTermsToUseOpen={onTermsToUseOpen}
                        handleImprintOpen={onImprintOpen}
                        handleFossOpen={onFossOpen}
                    />
                )}
                {isBellOpen && <BellNotification ref={bellRef} onClose={onBellClose} />}
                {isVersionOpen && <AppVersion onClose={onVersionClose} />}
            </div>
        </div>
    )
}

export default Header
